// janus-engine/minigames/use-key.ts — Lane L5 S1: "Unlock with Keys" / "Lock with Key" resolved in the browser.
//
// The browser chooses a carried key from its compiled links and the lock's held container/status rows.
// PHP records the decision under the lock-state CAS; it never chooses a different key.
import {
  asRow, findRow, inventoryIdOf, isUuid, lockIsLocked, lockIsLockedEitherWay, MINIGAME_TRUTH_SCHEMA, statusEntries, statusItemOf, LOCKED_STATUS_ID, upper, walkRows, type Row,
} from './rows.js';
import { scalar, type JanusCtx } from '../ability/context.js';
import { boundaryVerdict, resolveConnectionCascade, type GmBoundaryVerdict } from '../statuses/gm-boundary.js';
import { statusRemovalBefore } from '../statuses/expiry.js';
import { inventoryField, itemsCol } from '../../shared/array-types.js';

export interface UseKeyInput {
  encounterId: string; actorId: string; lockInventoryId: string; displayInventoryId?: string;
  action: 'unlock' | 'lock'; keyInventoryId?: string; rollPrivate?: number; expectedRev?: number;
}
export interface UseKeyReaders {
  /** The row's item_type (its `items` bucket, else its template's catalog definition). */
  itemType(row: Row): string;
  uuid(): string;
  /** Native removal context and the inventory-add planner's permanent row. */
  statusContext?: JanusCtx;
  statusAddition?: GmBoundaryVerdict;
}
export interface UseKeyCommand {
  schema: string; action: 'key'; operation_id: string; encounter_id: string; actor_id: string; expected_rev: number;
  key_action: 'unlock' | 'lock'; lock_inventory_id: string; display_inventory_id: string; key_inventory_id: string;
  expected_locked: boolean; roll_private: number;
  key_boundary?: GmBoundaryVerdict; lock_name: string; key_name: string;
}
export type UseKeyScope =
  | { supported: true; command: UseKeyCommand; lockFound: boolean; keyName: string }
  | { supported: false; refused: true; error: string; message: string };

export const USE_KEY_MESSAGES: Record<string, string> = {
  no_keys: 'You are not carrying any keys.',
  'key:display-bindings-not-held': 'This lock’s secured-object links are not available to the browser yet.',
  no_matching_key: 'None of your keys fit this lock.',
  wrong_key: 'None of your keys fit this lock.',
  not_locked: 'This is not locked.',
  already_locked: 'This is already locked.',
  key_not_held: 'You are no longer carrying that key.',
  lock_not_in_encounter: 'That lock is not here.',
  not_found: 'That key or lock no longer exists.',
  // a trapped lock: the unlock records the trap's browser-resolved fire in the same commit (minigame_truth_commit_inc.php)
  host_busy: 'The lock is busy; try again.',
  fire_verdict_required: 'The trap on this lock could not be resolved; try again.',
  fire_stale: 'The trap on this lock could not be resolved; try again.',
  fire_victim_gone: 'The trap on this lock could not be resolved; try again.',
  fire_unmodelled: 'The trap on this lock uses a rule the browser cannot resolve yet — it has been reported.',
};

/** Every key the actor carries, in carried order (keyring / bag contents at any depth), with the lock ids its
 * compiled `computed.key_links.opens` names. */
export function carriedKeys(actorRow: Row, read: UseKeyReaders): { id: string; name: string; opens: string[] }[] {
  const out: { id: string; name: string; opens: string[] }[] = [];
  const seen = new Set<string>();
  for (const { row } of walkRows(actorRow.inventory)) {
    const id = inventoryIdOf(row);
    if (!isUuid(id) || seen.has(id) || read.itemType(row) !== 'key') continue;
    seen.add(id);
    const opens = asRow(asRow(row.computed)?.key_links)?.opens;
    out.push({ id, name: String(inventoryField(row, 'inventory_name') || itemsCol(row, 'item_name') || 'Key'), opens: Array.isArray(opens) ? opens.map(upper) : [] });
  }
  return out;
}

export function resolveUseKey(input: UseKeyInput, encounter: Row, actorRow: Row, read: UseKeyReaders): UseKeyScope {
  const refuse = (error: string): UseKeyScope => ({ supported: false, refused: true, error, message: USE_KEY_MESSAGES[error] ?? error });
  const lockId = upper(input.lockInventoryId), displayId = upper(input.displayInventoryId);
  const relock = input.action === 'lock';
  const lock = findRow([encounter.encounter_inventories_arr, actorRow.inventory], lockId);
  if (!lock) return refuse('lock_not_in_encounter');
  if (!relock && !lockIsLocked(lock.row)) return refuse('not_locked');
  if (relock && lockIsLockedEitherWay(lock.row)) return refuse('already_locked');
  const keys = carriedKeys(actorRow, read);
  const explicit = upper(input.keyInventoryId);
  const wanted = new Set([lockId]);
  for (const status of statusEntries(lock.row, 'caster'))
    if (statusItemOf(status) === LOCKED_STATUS_ID) wanted.add(upper(scalar(status.status_target_id)));
  // The permanent relock is self-cast: the containing secured object remains a key binding after unlock removes its status.
  if (displayId && (displayId === lockId || lock.holders.includes(displayId))) wanted.add(displayId);
  if (!keys.length) return refuse('no_keys');
  const selected = explicit ? keys.find(k => k.id === explicit) : keys.find(k => k.opens.some(id => wanted.has(id)));
  if (explicit && !selected) return refuse('key_not_held');
  if (!selected || !selected.opens.some(id => wanted.has(id))) {
    if (relock && displayId && keys.some(k => k.opens.includes(displayId))) return refuse('key:display-bindings-not-held');
    return refuse('no_matching_key');
  }
  const keyName = selected.name;
  const lockedRows = statusEntries(lock.row, 'caster').filter(s => statusItemOf(s) === LOCKED_STATUS_ID);
  let changes = read.statusAddition;
  if (!relock && read.statusContext) {
    const ctx = read.statusContext;
    for (const status of lockedRows) {
      const id = upper(scalar(status.status_id)), target = upper(scalar(status.status_target_id)) || lockId;
      if (ctx.statusesRemoved.some(s => s.status_id === id)) continue;
      if (scalar(status.status_connected_id)) resolveConnectionCascade(ctx, status, target);
      // Connection-less historical rows still have an exact stored identity.
      if (!ctx.statusesRemoved.some(s => s.status_id === id)) ctx.statusesRemoved.push({ status_id: id,
        status_item_id: LOCKED_STATUS_ID, target, before: statusRemovalBefore(status, target) });
    }
    changes = boundaryVerdict(ctx);
  }
  if (changes) for (let i = 0; i < (relock ? 1 : lockedRows.length); i++) changes.rows.push({
    roll_type_1: relock ? 'status_effect_applied' : 'status_effect_removed', roll_type_2: null, roll_result: 0,
    roll_item_id: LOCKED_STATUS_ID, roll_by: upper(input.actorId), roll_to: lockId,
  });
  return {
    supported: true, lockFound: true, keyName,
    command: {
      schema: MINIGAME_TRUTH_SCHEMA, action: 'key', operation_id: read.uuid(), encounter_id: upper(input.encounterId), actor_id: upper(input.actorId),
      expected_rev: Math.max(0, Math.trunc(Number(input.expectedRev ?? 0)) || 0), key_action: relock ? 'lock' : 'unlock',
      lock_inventory_id: lockId, display_inventory_id: wanted.has(displayId) ? displayId : '', key_inventory_id: selected.id,
      expected_locked: lockIsLockedEitherWay(lock.row), roll_private: [0, 1, 2].includes(Number(input.rollPrivate)) ? Number(input.rollPrivate) : 0,
      lock_name: String(inventoryField(lock.row, 'inventory_name') || itemsCol(lock.row, 'item_name') || 'Lock'), key_name: keyName,
      ...(changes ? { key_boundary: changes } : {}),
    },
  };
}
